import React, { useEffect } from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { FormProvider, useForm } from 'react-hook-form';
import { DialogHeader } from 'components/shared/Dialog/DialogHeader';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { Button, Dialog, DialogActions, DialogContent, LinearProgress } from '@mui/material';
import {
  IntegrationSettingsForm,
  IntegrationSettingsTabs,
  IntegrationSettingsUserLevelForm,
} from 'modules/integrationSettings/types';
import { UserTab } from './userTab';
import { LocationTab } from './locationTab';
import { AccountTab } from './accountTab';
import { formatIntegrationSettingsFormDataToPayload } from '../../../lib';
import { useFetchIntegrationSettingsDialogData } from '../../../hooks/useFetchIntegrationSettingsDialogData';
import {
  InsertIntegrationSettingsValuesShema,
  INSERT_INTEGRATION_SETTINGS_VALUES_ARRAY,
  InsertIntegrationSettingsValuesVariables,
  InsertIntegrationSettingsValuesResponse,
} from '../../../api';

export const IntegrationSettingsDialog = () => {
  const { isOpen, data, handleClose } = useModal(ModalsKeys.IntegrationSettingsDialog);

  const { showNotification } = useNotifications();
  const vendorProfile = useSelector((state: any) => state.vp);

  const { integrationName, dealerAccountId, dealerAccountName, dealerContacts, dealerLocations } = data;

  const tabComponentByLevel: Record<
    IntegrationSettingsTabs,
    React.ComponentType<{
      integrationSettings: any;
      tableData: IntegrationSettingsUserLevelForm | any;
      vendorProfile?: any;
    }>
  > = {
    [IntegrationSettingsTabs.User]: UserTab,
    [IntegrationSettingsTabs.Location]: LocationTab,
    [IntegrationSettingsTabs.Account]: AccountTab,
  };

  const { isFetching, tabs, formData } = useFetchIntegrationSettingsDialogData(
    integrationName,
    dealerAccountId,
    dealerAccountName,
    dealerContacts,
    dealerLocations,
    tabComponentByLevel,
    vendorProfile
  );

  const form = useForm<IntegrationSettingsForm>();

  const [insertIntegrationSettingsValuesArray, { loading: isISVInsertLoading }] = useMutation<
    InsertIntegrationSettingsValuesResponse,
    InsertIntegrationSettingsValuesVariables
  >(INSERT_INTEGRATION_SETTINGS_VALUES_ARRAY);

  const handleSave = async (formData: IntegrationSettingsForm) => {
    try {
      const dto = formatIntegrationSettingsFormDataToPayload(integrationName, formData);

      const { data, success } = InsertIntegrationSettingsValuesShema.safeParse(dto);

      if (success) {
        await insertIntegrationSettingsValuesArray({ variables: { input: data } });
        showNotification('Changes Saved!');
      } else {
        showNotification('Error!', { type: 'error' });
      }

      handleClose();
    } catch (error) {
      console.error(error);
      showNotification('Error!', { type: 'error' });
    }
  };

  useEffect(() => {
    form.reset(formData);
  }, [formData]);

  const isLoading = isFetching || !tabs.length || isISVInsertLoading;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogHeader label="Integration Settings" handleClose={handleClose} />

      <DialogContent>
        {!isLoading && (
          <FormProvider {...form}>
            <TabsContainer<IntegrationSettingsTabs> tabs={tabs} />
          </FormProvider>
        )}
      </DialogContent>

      <DialogActions>
        {!isLoading ? (
          <>
            <Button onClick={handleClose} color="primary" variant="outlined">
              Cancel
            </Button>

            <Button onClick={form.handleSubmit(handleSave)} color="primary" variant="contained">
              Save
            </Button>
          </>
        ) : (
          <LinearProgress style={{ width: '100%' }} />
        )}
      </DialogActions>
    </Dialog>
  );
};
